import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#E84610', // Brand orange
      light: '#ff7043',
      dark: '#483C3B', // Brand dark neutral
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#FAB300', // Brand yellow/gold
      light: '#ffcc02',
      dark: '#c7930a',
      contrastText: '#000000',
    },
    error: {
      main: '#d32f2f',
    },
    warning: {
      main: '#FAB300', // Use brand yellow for warnings
    },
    info: {
      main: '#0288d1',
    },
    success: {
      main: '#2e7d32',
    },
    // Base colors
    common: {
      black: '#000000',
      white: '#FFFFFF',
    },
    // Text colors
    text: {
      primary: '#000000', // Base black
      secondary: '#483C3B', // Brand dark for secondary text
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    // Background colors
    background: {
      default: '#FFFFFF', // Base white
      paper: '#FFFFFF',
      neutral: '#EAE7E6', // Your existing neutral background
    },
    // Gray scale with your provided grays
    grey: {
      25: '#FAFEFB', // Secondary gray 25
      50: '#DEDFD1', // Secondary gray 50
      100: '#f8f9fa',
      300: '#e0e0e0',
      400: '#EAE7E6', // Your existing neutral
      500: '#483C3B', // Brand dark neutral
      900: '#000000', // Base black
    },
    // Custom brand colors for specific use cases
    brand: {
      orange: '#E84610',
      darkNeutral: '#483C3B',
      yellow: '#FAB300',
    },
  },
  typography: {
    fontFamily: 'Readex Pro, sans-serif',
    // Custom typography variants that match your dashboard
    h4: {
      fontSize: '24px',
      fontWeight: 300,
      color: '#000000', // Base black
      lineHeight: 1.2,
    },
    h6: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#000000', // Base black
    },
    body1: {
      fontSize: '14px',
      fontWeight: 400,
      color: '#000000', // Base black
    },
    body2: {
      fontSize: '14px',
      fontWeight: 400,
      color: '#483C3B', // Brand dark for secondary body text
    },
    // Custom variants for your specific use cases
    tableHeader: {
      fontSize: '14px',
      fontWeight: 600,
      color: '#000000', // Base black
    },
    tableCell: {
      fontSize: '14px',
      fontWeight: 400,
      color: '#000000', // Base black
    },
    navItem: {
      fontSize: '14px',
      fontWeight: 300,
      lineHeight: '20px',
      letterSpacing: '0%',
      color: '#483C3B', // Brand dark for navigation
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: 'Readex Pro, sans-serif',
        },
        contained: {
          backgroundColor: '#E84610', // Brand orange
          color: '#FFFFFF', // Base white
          boxShadow: '0px 3px 1px -2px #00000033, 0px 2px 2px 0px #00000024, 0px 1px 5px 0px #0000001F',
          '&:hover': {
            backgroundColor: '#483C3B', // Brand dark on hover
            boxShadow: '0px 3px 1px -2px #00000033, 0px 2px 2px 0px #00000024, 0px 1px 5px 0px #0000001F',
          },
        },
        outlined: {
          borderColor: '#DEDFD1', // Secondary gray 50
          color: '#483C3B', // Brand dark
          '&:hover': {
            borderColor: '#483C3B',
            backgroundColor: '#FAFEFB', // Secondary gray 25
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: 'Readex Pro, sans-serif',
          fontSize: '14px',
          borderBottom: '1px solid #DEDFD1', // Secondary gray 50
          padding: '12px 16px',
        },
        head: {
          fontWeight: 600,
          color: '#000000', // Base black
          backgroundColor: '#FAFEFB', // Secondary gray 25
        },
        body: {
          fontWeight: 400,
          color: '#000000', // Base black
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: 'Readex Pro, sans-serif',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            fontFamily: 'Readex Pro, sans-serif',
            fontSize: '14px',
            '& fieldset': {
              borderColor: '#DEDFD1', // Secondary gray 50
            },
            '&:hover fieldset': {
              borderColor: '#483C3B', // Brand dark
            },
            '&.Mui-focused fieldset': {
              borderColor: '#E84610', // Brand orange
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontFamily: 'Readex Pro, sans-serif',
          fontSize: '14px',
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          '& .MuiPaginationItem-root': {
            fontFamily: 'Readex Pro, sans-serif',
            fontSize: '14px',
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontFamily: 'Readex Pro, sans-serif',
          fontWeight: 300,
          fontSize: '14px',
          lineHeight: '20px',
          letterSpacing: '0%',
          color: '#483C3B', // Brand dark
        },
      },
    },
    // Paper component styling
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF', // Base white
          borderRadius: '8px',
        },
        // Don't apply border radius to certain Paper components
        '&.MuiTableContainer-root': {
          borderRadius: '8px',
          boxShadow: 'none',
        },
      },
    },
    // AppBar component styling - override Paper border radius
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: '0px', // Remove rounded corners from AppBar
          backgroundColor: '#483C3B', // Brand dark neutral
        },
      },
    },
  },
});

export default theme;